/**
 * SupplierInvoiceModal — COM-04: Crear Factura de Proveedor
 */
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import DatePicker from '@/components/DatePicker'
import { Loader2, Plus, Trash2, Building2 } from 'lucide-react'
import { type POLine } from '../hooks/usePurchases'
import { SupplierAutocomplete } from './SupplierAutocomplete'
import { CostCenterAllocator } from '@/modules/accounting/components/CostCenterAllocator'
import type { CostCenterAllocation } from '@/modules/accounting/components/CostCenterAllocator'
import { NumericInput } from '@/components/NumericInput'
import { formatMoneyForTenant, useTenantLocale, roundMoney, tenantCurrency } from '@/lib/locale'
import { inputCls } from '@/lib/formStyles'
import CustomFieldsForm, { type Personalizado } from '@/components/CustomFieldsForm'
import { useGlobalCustomFields, useTenantTax } from '@/modules/config/hooks/useConfig'
import { Modal } from '@/components/ds/Modal'

interface Props {
  onSave: (data: Record<string, unknown>) => void
  onClose: () => void
  isPending: boolean
  error?: string | null
}

type FormData = {
  supplier: string
  invoice_number: string
  issue_date: string
  due_date: string
  document_type_sii: number
  notes: string
}

interface LineWithCC extends POLine {
  cost_center_allocations?: CostCenterAllocation[]
}

interface Referencia {
  tipo_doc: string
  folio_ref: string
  fecha_ref: string
  cod_ref: string
  razon_ref: string
}

const DOC_TYPE_OPTIONS = [
  { value: 33, label: 'Factura Electrónica (33)' },
  { value: 34, label: 'Factura No Afecta (34)' },
  { value: 46, label: 'Factura de Compra (46)' },
  { value: 56, label: 'Nota de Débito (56)' },
  { value: 61, label: 'Nota de Crédito (61)' },
  { value: 110, label: 'Factura de Exportación (110)' },
  { value: 111, label: 'N.D. Exportación (111)' },
  { value: 112, label: 'N.C. Exportación (112)' },
]

const COD_REF_OPTIONS = [
  { value: '', label: '— Sin código —' },
  { value: '1', label: '1 — Anula documento' },
  { value: '2', label: '2 — Corrige texto' },
  { value: '3', label: '3 — Corrige monto' },
]

export function SupplierInvoiceModal({ onSave, onClose, isPending, error }: Props) {
  const locale = useTenantLocale()
  const tax = useTenantTax()
  // ⚠️ `IndExe` es el campo del DTE chileno: nombrarlo en un tenant peruano
  // manda a buscar algo que su comprobante no tiene. El nombre del impuesto sale
  // de la localización; la sigla solo se muestra donde efectivamente aplica.
  const exentoHint = tax.country === 'CL'
    ? `Marcar si la línea está exenta de ${tax.name} (IndExe del SII)`
    : `Marcar si la línea está exenta de ${tax.name}`
  const [lines, setLines] = useState<LineWithCC[]>([
    { sku: '', name: '', quantity: '1', unit_price: '0' },
  ])
  const [expandedCC, setExpandedCC] = useState<Set<number>>(new Set())
  const [referencias, setReferencias] = useState<Referencia[]>([])
  const [personalizados, setPersonalizados] = useState<Personalizado[]>([])
  const { data: comprasFields = [] } = useGlobalCustomFields(true, 'compras')

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: { supplier: '', invoice_number: '', issue_date: '', due_date: '', document_type_sii: 33, notes: '' },
  })

  const docTypeSii = Number(watch('document_type_sii') ?? 33)
  // 34 «factura no afecta o exenta» y 41 (boleta exenta) son exentos por
  // definición: la marca de cada línea es irrelevante, manda el tipo de DTE.
  const documentIsExempt = docTypeSii === 34 || docTypeSii === 41
  const isNCND = docTypeSii === 56 || docTypeSii === 61 || docTypeSii === 111 || docTypeSii === 112

  const addReferencia = () => setReferencias(r => [...r, {
    tipo_doc: '33', folio_ref: '', fecha_ref: '', cod_ref: '1', razon_ref: '',
  }])
  const updateReferencia = (i: number, field: keyof Referencia, value: string) =>
    setReferencias(r => r.map((ref, idx) => idx === i ? { ...ref, [field]: value } : ref))
  const removeReferencia = (i: number) =>
    setReferencias(r => r.filter((_, idx) => idx !== i))

  const addLine = () => setLines(l => [...l, { sku: '', name: '', quantity: '1', unit_price: '0' }])
  const removeLine = (i: number) => {
    setLines(l => l.filter((_, idx) => idx !== i))
    setExpandedCC(prev => { const next = new Set(prev); next.delete(i); return next })
  }
  const updateLine = (i: number, field: keyof POLine, value: string) =>
    setLines(l => l.map((line, idx) => idx === i ? { ...line, [field]: value } : line))

  const toggleExempt = (i: number, value: boolean) =>
    setLines(l => l.map((x, idx) => (idx === i ? { ...x, is_tax_exempt: value } : x)))

  const updateLineCC = (i: number, allocs: CostCenterAllocation[]) =>
    setLines(l => l.map((line, idx) => idx === i ? { ...line, cost_center_allocations: allocs } : line))

  const toggleCC = (i: number) => {
    setExpandedCC(prev => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  // Exento (reportado por Beatriz, 2026-08-03). El estándar del SII tiene dos
  // escenarios y los dos se cubren con el mismo mecanismo, igual que en ventas:
  //   - tipo 34 «no afecta o exenta» y 41 (boleta exenta): TODO el documento es
  //     exento, el monto va en MntExe de la cabecera y no hay IVA.
  //   - tipo 33: las líneas exentas llevan IndExe y MntExe es su SUMA.
  // Antes esto calculaba 19% sobre todo el subtotal sin mirar la marca, así que
  // una compra exenta quedaba con IVA crédito fiscal inventado.
  const lineSubtotal = (l: LineWithCC) =>
    (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)
  const isExemptLine = (l: LineWithCC) => documentIsExempt || !!l.is_tax_exempt
  const subtotal = lines.reduce((acc, l) => acc + lineSubtotal(l), 0)
  const exemptSubtotal = lines.reduce(
    (acc, l) => acc + (isExemptLine(l) ? lineSubtotal(l) : 0), 0)
  const taxableSubtotal = subtotal - exemptSubtotal
  // La tasa sale de Configuración → Localización, que es la misma que usa el
  // backend para recalcular (`purchases/serializers.py`). Estaba escrita 0.19,
  // así que en un tenant con IGV 18% la pantalla mostraba un total y se
  // guardaba otro, sin ningún aviso.
  const taxAmount = roundMoney(taxableSubtotal * tax.rate / 100, tenantCurrency())
  const total = subtotal + taxAmount

  const onSubmit = (data: FormData) => {
    const payload: Record<string, unknown> = {
      supplier: data.supplier,
      invoice_number: data.invoice_number,
      issue_date: data.issue_date,
      due_date: data.due_date || null,
      document_type_sii: Number(data.document_type_sii ?? 33),
      notes: data.notes || '',
      lines: lines.map(l => {
        const base: Record<string, unknown> = {
          sku: l.sku, name: l.name, quantity: l.quantity, unit_price: l.unit_price,
          is_tax_exempt: isExemptLine(l),
        }
        if (l.cost_center_allocations?.length) {
          base.cost_center_allocations = l.cost_center_allocations
        }
        return base
      }),
    }
    // Solo enviar referencias si el tipo es NC/ND (56, 61, 111, 112)
    if (isNCND && referencias.length > 0) {
      payload.referencias = referencias.filter(r => r.folio_ref)
    }
    if (personalizados.length > 0) {
      payload.personalizados = personalizados
    }
    onSave(payload)
  }

  return (
    <Modal open onClose={onClose} title="Registrar Factura de Proveedor" size="2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="supplierinvoicemodal-supplier" className="block text-xs font-medium text-content-muted mb-1">Proveedor *</label>
            <Controller name="supplier" control={control} rules={{ required: 'Requerido' }}
              render={({ field }) => (
                <SupplierAutocomplete id="supplierinvoicemodal-supplier"
                  value={field.value} onChange={field.onChange} className={inputCls} />
              )} />
            {errors.supplier && <p className="text-xs text-danger-solid mt-0.5">{errors.supplier.message}</p>}
          </div>
          <div>
            <label htmlFor="supplierinvoicemodal-document-type-sii" className="block text-xs font-medium text-content-muted mb-1">Tipo de Documento</label>
            <select id="supplierinvoicemodal-document-type-sii" {...register('document_type_sii', { valueAsNumber: true })} className={inputCls}>
              {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="supplierinvoicemodal-invoice-number" className="block text-xs font-medium text-content-muted mb-1">Nro. Factura *</label>
            <input id="supplierinvoicemodal-invoice-number" {...register('invoice_number', { required: 'Requerido' })} className={inputCls} placeholder="F-001 / 0000123" />
            {errors.invoice_number && <p className="text-xs text-danger-solid mt-0.5">{errors.invoice_number.message}</p>}
          </div>
          <div>
            <label htmlFor="supplierinvoicemodal-issue-date" className="block text-xs font-medium text-content-muted mb-1">Fecha de Emisión *</label>
            <Controller name="issue_date" control={control} rules={{ required: 'Requerido' }} render={({ field }) => <DatePicker id="supplierinvoicemodal-issue-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
            {errors.issue_date && <p className="text-xs text-danger-solid mt-0.5">{errors.issue_date.message}</p>}
          </div>
          <div>
            <label htmlFor="supplierinvoicemodal-due-date" className="block text-xs font-medium text-content-muted mb-1">Fecha de Vencimiento</label>
            <Controller name="due_date" control={control} render={({ field }) => <DatePicker id="supplierinvoicemodal-due-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
          </div>
          <div>
            <label htmlFor="supplierinvoicemodal-notes" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
            <input id="supplierinvoicemodal-notes" {...register('notes')} className={inputCls} placeholder="Observaciones..." />
          </div>
        </div>

        {/* Campos personalizados (compra) */}
        {comprasFields.length > 0 && (
          <div className="border-t border-divider pt-3">
            <label className="block text-xs font-medium text-content-muted mb-2">Campos personalizados</label>
            <CustomFieldsForm fields={comprasFields} value={personalizados} onChange={setPersonalizados} />
          </div>
        )}

        {/* Líneas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-content-muted">Líneas</label>
            <button type="button" onClick={addLine}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover">
              <Plus size={12} />Agregar línea
            </button>
          </div>
          <div className="space-y-2">
            {lines.map((line, i) => (
              <div key={i} className="space-y-1">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <input aria-label={`SKU — línea ${i + 1}`} value={line.sku} onChange={e => updateLine(i, 'sku', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="SKU" />
                  <input aria-label={`Descripción — línea ${i + 1}`} value={line.name} onChange={e => updateLine(i, 'name', e.target.value)}
                    className="col-span-3 px-2 py-1.5 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Descripción" />
                  <NumericInput aria-label={`Cantidad — línea ${i + 1}`} value={line.quantity}
                    onChange={v => updateLine(i, 'quantity', v)}
                    locale={locale} decimals={2}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-right"
                    placeholder="Cant." />
                  <NumericInput aria-label={`Precio neto — línea ${i + 1}`} value={line.unit_price}
                    onChange={v => updateLine(i, 'unit_price', v)}
                    locale={locale} decimals={2}
                    className="col-span-3 px-2 py-1.5 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-right"
                    placeholder="P. Neto" />
                  <label className="col-span-1 flex flex-col items-center gap-0.5 cursor-pointer"
                    title={documentIsExempt
                      ? 'El tipo de documento es exento: todas sus líneas lo son'
                      : exentoHint}>
                    <span className="text-[10px] leading-none text-content-subtle">Exento</span>
                    <input type="checkbox" checked={isExemptLine(line)}
                      disabled={documentIsExempt}
                      onChange={e => toggleExempt(i, e.target.checked)}
                      className="rounded border-divider text-primary focus:ring-focus-ring disabled:opacity-60" />
                  </label>
                  <button type="button" onClick={() => removeLine(i)}
                    className="col-span-1 p-1.5 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded-lg justify-self-center">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs pl-1">
                  <button
                    type="button"
                    onClick={() => toggleCC(i)}
                    className={`inline-flex items-center gap-0.5 transition-colors ${
                      expandedCC.has(i) || (line.cost_center_allocations?.length ?? 0) > 0
                        ? 'text-info-content-soft hover:text-info-content-strong dark:text-violet-200'
                        : 'text-primary hover:text-primary-hover'
                    }`}
                  >
                    <Building2 size={11} />
                    CC
                    {(line.cost_center_allocations?.length ?? 0) > 0 && (
                      <span className="text-[10px] bg-info-surface-strong text-info-content px-1 rounded">
                        {line.cost_center_allocations!.length}
                      </span>
                    )}
                  </button>
                </div>
                {expandedCC.has(i) && (
                  <div className="bg-violet-50/50 border border-info-border rounded-md p-2 ml-1">
                    <p className="text-[10px] text-info-solid mb-1 font-medium uppercase tracking-wider">
                      Centros de Costo
                    </p>
                    <CostCenterAllocator
                      allocations={line.cost_center_allocations ?? []}
                      onChange={(allocs) => updateLineCC(i, allocs)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Referencias (NC/ND) */}
        {isNCND && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-xs font-medium text-content-muted">Referencias</label>
                <p className="text-[10px] text-content-subtle">Documentos originales que esta NC/ND modifica.</p>
              </div>
              <button type="button" onClick={addReferencia}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover">
                <Plus size={12} />Agregar referencia
              </button>
            </div>
            {referencias.length === 0 && (
              <p className="text-xs text-warning-content-soft bg-warning-surface border border-warning-border rounded-md px-3 py-2">
                Al menos una referencia debería indicar qué factura original afecta esta {docTypeSii === 56 || docTypeSii === 111 ? 'ND' : 'NC'}.
              </p>
            )}
            <div className="space-y-2">
              {referencias.map((ref, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select aria-label={`Tipo de documento — referencia ${i + 1}`} value={ref.tipo_doc} onChange={e => updateReferencia(i, 'tipo_doc', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg">
                    <option value="33">33 FE</option>
                    <option value="34">34 FNA</option>
                    <option value="46">46 FC</option>
                    <option value="110">110 FEX</option>
                  </select>
                  <input aria-label={`Folio — referencia ${i + 1}`} value={ref.folio_ref} onChange={e => updateReferencia(i, 'folio_ref', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg"
                    placeholder="Folio" />
                  <input aria-label={`Fecha — referencia ${i + 1}`} type="date" value={ref.fecha_ref} onChange={e => updateReferencia(i, 'fecha_ref', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg" />
                  <select aria-label={`Código de referencia — referencia ${i + 1}`} value={ref.cod_ref} onChange={e => updateReferencia(i, 'cod_ref', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg">
                    {COD_REF_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input aria-label={`Razón — referencia ${i + 1}`} value={ref.razon_ref} onChange={e => updateReferencia(i, 'razon_ref', e.target.value)}
                    className="col-span-3 px-2 py-1.5 text-sm border border-divider rounded-lg"
                    placeholder="Razón" />
                  <button type="button" onClick={() => removeReferencia(i)}
                    className="col-span-1 p-1.5 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded-lg justify-self-center">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Totales */}
        <div className="bg-glass-3 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between text-content-muted">
            <span>Subtotal neto</span>
            <span>{formatMoneyForTenant(subtotal)}</span>
          </div>
          {/* El desglose solo aparece cuando hay algo exento: en una factura
              afecta normal sería ruido. */}
          {exemptSubtotal > 0 && (
            <>
              <div className="flex justify-between text-content-subtle">
                <span className="pl-3">Afecto a {tax.name}</span>
                <span>{formatMoneyForTenant(taxableSubtotal)}</span>
              </div>
              <div className="flex justify-between text-content-subtle">
                <span className="pl-3">Exento (MntExe)</span>
                <span>{formatMoneyForTenant(exemptSubtotal)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between text-content-muted">
            <span>{`${tax.name} (${tax.rate}%${exemptSubtotal > 0 ? ' del afecto' : ''})`}</span>
            <span>{formatMoneyForTenant(taxAmount)}</span>
          </div>
          <div className="flex justify-between font-semibold text-content border-t border-divider pt-2">
            <span>Total</span>
            <span>{formatMoneyForTenant(total)}</span>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={13} className="animate-spin" />}
            Registrar Factura
          </button>
        </div>
      </form>
    </Modal>
  )
}
